import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { PLANS, formatINR } from '../lib/plans';
import { assets } from '../lib/assets';

// A different planting visual per tier to convey growing scale
const planBgImage = {
  sapling: assets.seedlingHand,
  care: assets.plantingHands,
  lifetime: assets.treePlanting,
  family: assets.matureTree,
};

export default function Pricing({ onSelectPlan }) {
  return (
    <section id="pricing" className="bg-brand-bg py-28 md:py-36" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <p className="text-brand-gold tracking-[0.25em] uppercase text-xs mb-5 font-medium">Choose your plan</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-[1.05] tracking-tight font-light">
              Four ways to <em className="text-brand-forest">plant a story.</em>
            </h2>
          </div>
          <p className="md:max-w-sm text-brand-ink2 leading-relaxed">
            From a single sapling to a lifetime of fruit — there's a right depth of legacy for every occasion.
          </p>
        </div>

        {/* Tier cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PLANS.map((p, i) => (
            <motion.div
              key={p.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
              whileHover={{ y: -6 }}
              className={`group relative flex flex-col rounded-3xl overflow-hidden pb-7 md:pb-8 px-7 md:px-8 pt-7 md:pt-8 transition-all duration-500 ${
                p.featured
                  ? 'bg-brand-forest text-brand-bg shadow-2xl shadow-brand-forest/20'
                  : 'bg-brand-bg2 border border-brand-line hover:border-brand-gold/60'
              }`}
              data-testid={`plan-card-${p.key}`}
            >
              {p.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-brand-gold text-brand-ink text-[10px] tracking-[0.25em] uppercase font-medium z-20">
                  Most loved
                </div>
              )}

              {/* Tree-planting image behind icon */}
              <div className="relative h-32 -mx-7 md:-mx-8 -mt-7 md:-mt-8 mb-6 overflow-hidden rounded-t-3xl">
                <img
                  src={planBgImage[p.key]}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className={`absolute inset-0 ${p.featured ? 'bg-gradient-to-b from-brand-forest/40 via-brand-forest/60 to-brand-forest' : 'bg-gradient-to-b from-brand-ink/20 via-brand-bg2/40 to-brand-bg2'}`} />
                <div className="absolute bottom-3 left-7 md:left-8 text-4xl drop-shadow-sm">{p.icon}</div>
              </div>

              <div className="mb-6">
                <div className={`font-serif text-2xl md:text-[26px] font-medium leading-tight ${p.featured ? 'text-brand-bg' : 'text-brand-ink'}`}>
                  {p.name}
                </div>
                <div className={`font-serif italic text-sm mt-1 ${p.featured ? 'text-brand-gold' : 'text-brand-gold'}`}>
                  {p.tagline}
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className={`font-serif text-5xl font-light ${p.featured ? 'text-brand-bg' : 'text-brand-ink'}`}>
                    {formatINR(p.price)}
                  </span>
                </div>
                {p.duration && (
                  <div className={`text-xs tracking-widest uppercase mt-1 ${p.featured ? 'text-brand-bg/85' : 'text-brand-ink2'}`}>
                    {p.duration}
                  </div>
                )}
              </div>

              <ul className="space-y-2.5 mb-5 flex-1">
                {p.includes.map((item) => (
                  <li key={item} className={`flex items-start gap-2.5 text-[13.5px] leading-relaxed ${p.featured ? 'text-brand-bg' : 'text-brand-ink'}`}>
                    <Check className={`w-3.5 h-3.5 mt-1 flex-shrink-0 ${p.featured ? 'text-brand-gold' : 'text-brand-forest'}`} strokeWidth={2.5} />
                    <span>{item}</span>
                  </li>
                ))}
                {p.note && (
                  <li className={`text-[11.5px] italic leading-relaxed pt-1 pl-6 ${p.featured ? 'text-brand-bg/80' : 'text-brand-ink2'}`}>
                    Note: {p.note}
                  </li>
                )}
              </ul>

              {p.for && (
                <div className={`text-[11px] italic mb-5 leading-relaxed ${p.featured ? 'text-brand-bg/70' : 'text-brand-ink2'}`}>
                  For — {p.for}
                </div>
              )}

              <button
                onClick={() => onSelectPlan(p.key)}
                data-testid={`plan-select-${p.key}`}
                className={`rounded-full px-5 py-3 text-sm tracking-wide font-medium transition-all hover:-translate-y-0.5 ${
                  p.featured
                    ? 'bg-brand-gold text-brand-ink hover:bg-brand-bg'
                    : 'bg-brand-forest text-brand-bg hover:bg-brand-ink'
                }`}
              >
                Gift this plan →
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
